// client/src/utils/pokerStats/helpers.ts
//
// Funciones puras sin dependencias de dominio ni side effects.
// Todas testeables en isolation.

import { Position, FlopTexture, StackBucket } from './types';
import { POSITION_ORDER, RANK_ORDER, STACK_BUCKET_SHORT_MAX, STACK_BUCKET_MID_MAX } from './constants';

// ─────────────────────────────────────────────────────────────────────────────
// parsePokerNumber — soporta US ($1,050.50), EU (1.050,50), simple (17.92),
// y strings con trailing punctuation (17.92.)
// ─────────────────────────────────────────────────────────────────────────────
export function parsePokerNumber(str: string | undefined | null): number {
  if (!str) return 0;

  let clean = String(str)
    .replace(/[$¥\s]/g, '')
    .replace(/[.,]+$/, '') // strip trailing punctuation (sentence endings)
    .trim();

  if (!clean) return 0;

  // Si hay AMBOS separadores, el más a la derecha es el decimal
  if (clean.includes(',') && clean.includes('.')) {
    if (clean.lastIndexOf(',') > clean.lastIndexOf('.')) {
      // Formato europeo: 1.234,56
      clean = clean.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato US: 1,234.56
      clean = clean.replace(/,/g, '');
    }
  } else if (clean.includes(',')) {
    // Solo comas. Una coma con 1-2 dígitos atrás = decimal europeo.
    // Múltiples comas o 3+ dígitos atrás = separador de miles.
    const afterComma = clean.length - clean.lastIndexOf(',') - 1;
    const commaMatches = clean.match(/,/g);
    const commaCount = commaMatches ? commaMatches.length : 0;
    if (commaCount === 1 && afterComma >= 1 && afterComma <= 2) {
      clean = clean.replace(',', '.');
    } else {
      clean = clean.replace(/,/g, '');
    }
  }

  return parseFloat(clean) || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Regex escape — para construir regex con input dinámico (nombres de jugadores)
// ─────────────────────────────────────────────────────────────────────────────
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ─────────────────────────────────────────────────────────────────────────────
// classifyFlopTexture — simple pero útil para segmentar cbets
// ─────────────────────────────────────────────────────────────────────────────
export function classifyFlopTexture(flopText: string): FlopTexture {
  try {
    const m = flopText.match(/\[([^\]]+)\]/);
    if (!m) return 'dry';
    const cards = m[1].trim().split(/\s+/).map(c => c.trim());
    if (cards.length < 3) return 'dry';

    const ranks = cards.map(c => c[0]);
    const suits = cards.map(c => c[1]);

    // Paired
    if (ranks[0] === ranks[1] || ranks[0] === ranks[2] || ranks[1] === ranks[2]) {
      return 'paired';
    }

    // Suit counts
    const suitCounts: Record<string, number> = {};
    for (const s of suits) suitCounts[s] = (suitCounts[s] || 0) + 1;
    const maxSuit = Math.max(...Object.values(suitCounts));
    if (maxSuit >= 3) return 'monotone';

    // Rank gaps
    const rnums = ranks.map(r => RANK_ORDER.indexOf(r)).sort((a, b) => a - b);
    const gap01 = Math.abs(rnums[1] - rnums[0]);
    const gap12 = Math.abs(rnums[2] - rnums[1]);

    if (maxSuit === 2) {
      // Flush draw present
      if (gap01 <= 2 || gap12 <= 2) return 'wet';
      return 'semi-wet';
    }

    // Rainbow
    if (gap01 <= 1 || gap12 <= 1) return 'semi-wet';
    return 'dry';
  } catch {
    return 'dry';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Stack bucket
// ─────────────────────────────────────────────────────────────────────────────
export function getStackBucket(bb: number): StackBucket {
  if (bb < STACK_BUCKET_SHORT_MAX) return 'short';
  if (bb <= STACK_BUCKET_MID_MAX) return 'mid';
  return 'deep';
}

// ─────────────────────────────────────────────────────────────────────────────
// Position detection — hero
// ─────────────────────────────────────────────────────────────────────────────
export function getHeroPosition(block: string): Position {
  const btnM = block.match(/Seat #(\d+) is the button/);
  const heroM = block.match(/^Seat (\d+): Hero /m);
  const allS = block.match(/^Seat (\d+): \w/gm);
  if (!btnM || !heroM || !allS) return 'OTHER';

  const btnSeat = parseInt(btnM[1]);
  const heroSeat = parseInt(heroM[1]);
  const seats = Array.from(
    new Set(
      allS.map((s: string) => {
        const m = s.match(/Seat (\d+)/);
        return m ? parseInt(m[1]) : 0;
      })
    )
  ).sort((a, b) => a - b);

  const n = seats.length;
  if (n === 0) return 'OTHER';

  const btnIdx = seats.indexOf(btnSeat);
  const heroIdx = seats.indexOf(heroSeat);

  if (heroSeat === btnSeat) return 'BTN';
  if (heroIdx === (btnIdx + 1) % n) return 'SB';
  if (heroIdx === (btnIdx + 2) % n) return 'BB';

  const fromBtn = (btnIdx - heroIdx + n) % n;
  if (fromBtn === 1) return 'CO';
  if (fromBtn === 2) return 'HJ';
  return 'EP';
}

// ─────────────────────────────────────────────────────────────────────────────
// Position detection — arbitrary player by name
// ─────────────────────────────────────────────────────────────────────────────
export function getPlayerPosition(block: string, playerName: string): Position {
  if (!playerName || playerName === '') return 'OTHER';

  const btnM = block.match(/Seat #(\d+) is the button/);
  const playerRe = new RegExp(`^Seat (\\d+): ${escapeRegex(playerName)} `, 'm');
  const playerM = playerRe.exec(block);
  const allS = block.match(/^Seat (\d+): [^(\r\n]+/gm);

  if (!btnM || !playerM || !allS) return 'OTHER';

  const btnSeat = parseInt(btnM[1]);
  const playerSeat = parseInt(playerM[1]);

  const seats = allS
    .map(s => {
      const m = s.match(/Seat (\d+)/);
      return m ? parseInt(m[1]) : 0;
    })
    .sort((a, b) => a - b);
  const uniqueSeats = Array.from(new Set(seats));

  const n = uniqueSeats.length;
  const playerIdx = uniqueSeats.indexOf(playerSeat);
  const btnIdx = uniqueSeats.indexOf(btnSeat);
  const dist = (playerIdx - btnIdx + n) % n;

  if (dist === 0) return 'BTN';
  if (n === 2) return dist === 1 ? 'BB' : 'BTN';
  if (dist === 1) return 'SB';
  if (dist === 2) return 'BB';
  if (dist === n - 1) return 'CO';
  if (dist === n - 2) return 'HJ';
  return 'EP';
}

// ─────────────────────────────────────────────────────────────────────────────
// Is hero in position vs villain? Higher index in POSITION_ORDER = later = IP.
// ─────────────────────────────────────────────────────────────────────────────
export function isHeroIP(heroPos: Position, villainPos: Position): boolean {
  const score = (p: Position) => {
    const idx = POSITION_ORDER.indexOf(p);
    return idx === -1 ? -1 : idx;
  };
  return score(heroPos) > score(villainPos);
}

// ─────────────────────────────────────────────────────────────────────────────
// Detect if a player's last raise in the text was all-in
// Used for Fase 2 (fold to 3bet split)
// ─────────────────────────────────────────────────────────────────────────────
export function wasLastRaiseAllIn(text: string, player: string): boolean {
  const re = new RegExp(`^${escapeRegex(player)}: raises [^\\n]*\\band is all-in\\b`, 'm');
  return re.test(text);
}

// ─────────────────────────────────────────────────────────────────────────────
// Extract actions — returns array of [player, action]
// ─────────────────────────────────────────────────────────────────────────────
export function getActions(text: string): Array<[string, string]> {
  return Array.from(
    text.matchAll(/^([A-Za-z0-9_][A-Za-z0-9_-]*): (raises|calls|folds|checks|bets)/gm)
  ).map(m => [m[1], m[2]] as [string, string]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Flop villains — players (not Hero) who went to the flop
// ─────────────────────────────────────────────────────────────────────────────
export function getFlopVillains(flopActions: Array<[string, string]>): string[] {
  const set = new Set<string>();
  for (const [p] of flopActions) {
    if (p !== 'Hero') set.add(p);
  }
  return Array.from(set);
}
